import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

export const useUsers = (
  page = 1,
  limit = 50,
  searchKey = "",
  searchValue = "",
  sortBy = "createdAt",
  sortOrder = "desc"
) => {
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ["users", page, limit, searchKey, searchValue, sortBy, sortOrder],
    queryFn: async () => {
      const params = {
        page,
        limit,
        sortBy,
        sortOrder,
      };

      if (searchKey && searchValue) {
        params.searchKey = searchKey;
        params.searchValue = searchValue;
      }

      const queryString = new URLSearchParams(params).toString();

      try {
        const response = await apiClient.get(`/users?${queryString}`);

        const payload = response.data?.data || {}; 
        
        const usersData = Array.isArray(payload.data) ? payload.data : [];
        const paginationData = payload.pagination || {};

        return {
          users: usersData,
          pagination: {
            totalRecords: paginationData.totalRecords || 0,
            totalPages: paginationData.totalPages || 1,
            currentPage: paginationData.currentPage || 1,
            signedInUsers: paginationData.signedInUsers || 0,
          },
        };
      } catch (err) {
        console.error("API Error:", err);
        throw err;
      }
    },
    keepPreviousData: true,
    staleTime: 5000, 
  });
  const deleteUserMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`/users/${id}`);
    },
    onSuccess: () => {
      toast.success("User deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete user");
    },
  });

  const downloadCSVMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get("/users/download", {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: () => {
      toast.error("Failed to download CSV");
    },
  });

  return {
    users: usersQuery.data?.users || [],
    pagination: usersQuery.data?.pagination || { totalPages: 1, totalRecords: 0 },
    isLoading: usersQuery.isLoading,
    isFetching: usersQuery.isFetching,
    isError: usersQuery.isError,
    
    deleteUser: deleteUserMutation.mutateAsync,
    isDeleting: deleteUserMutation.isPending,
    
    downloadUsersCSV: downloadCSVMutation.mutate,
    isDownloadingCSV: downloadCSVMutation.isPending,
  };
};